/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Eye, EyeOff, Mail, Lock, User, Phone, ChevronRight,
  ShieldCheck, Users, Briefcase, Check, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthHeader from "@/components/AuthHeader";
import GoogleButton from "@/components/GoogleButton";
import { useAuth, formatAuthError } from "@/context/AuthContext";
import ProviderRegionPicker from "@/components/ProviderRegionPicker";
import { ApprovalConsent } from "@/components/LegalConsent";
import { KVKK_TEXT, ACIK_RIZA_TEXT, CEREZ_TEXT, AYDINLATMA_TEXT } from "@/lib/legalTexts";

// Firma (kurumsal) kaydına yalnız bu kategoride izin verilir; backend'de de zorlanır.
const FIRMA_CATEGORY = "Halı & Koltuk Yıkama";

// Uzmanlıklar artık kategoriye (dikeye) özel. Sağlayıcı hangi alanları seçtiyse
// yalnız o alanların uzmanlıkları gösterilir.
const SPECIALTIES_BY_CATEGORY = {
  "Ev Temizliği": [
    "Standart Temizlik",
    "Derin Temizlik",
    "Cam Silme",
    "İnşaat Sonrası Temizlik",
  ],
  "Halı & Koltuk Yıkama": [
    "Yerinde koltuk yıkama",
    "Halı yıkama",
  ],
};


export default function Register() {
  const { register, signInWithGoogle, completeSocialRegistration, fbUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Sosyal tamamlama modu: Google ile giriş yapılmış ama profil yok
  // (/kayit?social=1 + aktif Firebase oturumu). E-posta/şifre alanları gizlenir.
  const socialMode = params.get("social") === "1" && !!fbUser;
  // Kayıt sonrası dönülecek yer (booking akışından ?next= ile gelir).
  const nextUrl = params.get("next") || "";
  const girisHref = nextUrl ? `/giris?next=${encodeURIComponent(nextUrl)}` : "/giris";
  const [role, setRole] = useState(null); // "musteri" | "saglayici"
  const [form, setForm] = useState({
    ad_soyad: "",
    email: "",
    telefon: "",
    sifre: "",
    sifre_tekrar: "",
  });
  const [bolgeler, setBolgeler] = useState([]);
  const [uzmanliklar, setUzmanliklar] = useState([]);
  const [kategoriler, setKategoriler] = useState(["Ev Temizliği"]);
  const [allCategories, setAllCategories] = useState(["Ev Temizliği", "Halı & Koltuk Yıkama"]);
  // Faz 2 — Sağlayıcı tipi KATEGORİDEN belirlenir (sabit kural):
  //   Halı & Koltuk Yıkama → FİRMA (zorunlu)   •   Ev Temizliği → BİREYSEL
  // Kategori tek seçimdir; ikisi birden seçilemez.
  const [firma, setFirma] = useState({ firma_adi: "", firma_vergi_no: "", firma_vergi_dairesi: "", firma_iban: "" });
  const setFirmaField = (k) => (e) => setFirma((f) => ({ ...f, [k]: e.target.value }));
  const isCarpet = kategoriler.includes(FIRMA_CATEGORY);
  // Seçili kategorilere göre gösterilecek uzmanlıklar (birleşim, tekilleştirilmiş).
  const activeSpecialties = (kategoriler.length ? kategoriler : ["Ev Temizliği"])
    .flatMap((c) => SPECIALTIES_BY_CATEGORY[c] || [])
    .filter((s, i, a) => a.indexOf(s) === i);
  // Kategori değişince artık geçerli olmayan uzmanlık seçimlerini temizle.
  useEffect(() => {
    setUzmanliklar((prev) => prev.filter((u) =>
      (kategoriler.length ? kategoriler : ["Ev Temizliği"]).some((c) => (SPECIALTIES_BY_CATEGORY[c] || []).includes(u))
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoriler]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d?.categories) && d.categories.length) setAllCategories(d.categories); })
      .catch(() => {});
  }, []);
  const [kvkk, setKvkk] = useState(false);
  const [acikRiza, setAcikRiza] = useState(false);
  const [cerez, setCerez] = useState(false);
  const [aydinlatma, setAydinlatma] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Sosyal modda ad soyad'ı Google profilinden ön-doldur.
  useEffect(() => {
    if (socialMode && fbUser?.displayName) {
      setForm((f) => (f.ad_soyad ? f : { ...f, ad_soyad: fbUser.displayName, email: fbUser.email || "" }));
    }
  }, [socialMode, fbUser]);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const r = await signInWithGoogle();
      if (r.status === "existing") {
        const u = r.user;
        toast.success(`Hoş geldin, ${(u.ad_soyad || "").split(" ")[0] || "PAK üyesi"}!`);
        navigate(nextUrl || (u.role === "admin" ? "/admin" : u.role === "saglayici" ? "/panel" : "/"), { replace: true });
      } else {
        toast("Neredeyse tamam — birkaç bilgi daha alalım.");
        navigate(`/kayit?social=1${nextUrl ? `&next=${encodeURIComponent(nextUrl)}` : ""}`, { replace: true });
      }
    } catch (err) {
      if (["auth/popup-closed-by-user", "auth/cancelled-popup-request"].includes(err?.code)) return;
      toast.error(formatAuthError(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!role) return toast.error("Lütfen hesap türünü seçin.");
    if (!form.ad_soyad.trim()) return toast.error("Ad Soyad girin.");
    if (!socialMode && !form.email.trim()) return toast.error("E-posta girin.");
    if (!form.telefon.trim()) return toast.error("Telefon numaranızı girin.");
    if (!socialMode && form.sifre.length < 6) return toast.error("Şifre en az 6 karakter olmalıdır.");
    if (!socialMode && form.sifre !== form.sifre_tekrar) return toast.error("Şifreler eşleşmiyor.");
    if (role === "saglayici") {
      if (kategoriler.length === 0) return toast.error("En az bir hizmet alanı seçin.");
      if (bolgeler.length === 0) return toast.error("En az bir hizmet bölgesi seçin.");
      if (uzmanliklar.length === 0) return toast.error("En az bir uzmanlık alanı seçin.");
      if (isCarpet) {
        if (!firma.firma_adi.trim()) return toast.error("Firma adını girin.");
        if (!firma.firma_vergi_no.trim()) return toast.error("Vergi numarasını girin.");
        if (!firma.firma_vergi_dairesi.trim()) return toast.error("Vergi dairesini girin.");
        if (!/^TR\d{24}$/.test(firma.firma_iban.replace(/\s/g, "").toUpperCase())) return toast.error("Geçerli bir TR IBAN girin (TR + 24 rakam).");
      }
    }
    if (!kvkk) return toast.error("Devam etmek için KVKK metnini onaylamanız gerekir.");
    if (!acikRiza) return toast.error("Devam etmek için Açık Rıza Metni'ni onaylamanız gerekir.");
    if (!aydinlatma) return toast.error("Devam etmek için Aydınlatma Metni'ni okumanız gerekir.");
    if (!cerez) return toast.error("Devam etmek için Çerez Politikası'nı okumanız gerekir.");

    setSubmitting(true);
    try {
      const base = {
        ad_soyad: form.ad_soyad.trim(),
        telefon: form.telefon.trim(),
        role,
        kvkk_onay: true,
        acik_riza_onay: true,
        ...(role === "saglayici"
          ? {
              bolgeler,
              uzmanliklar,
              kategoriler,
              saglayici_tipi: isCarpet ? "firma" : "bireysel",
              ...(isCarpet
                ? {
                    firma_adi: firma.firma_adi.trim(),
                    firma_vergi_no: firma.firma_vergi_no.trim(),
                    firma_vergi_dairesi: firma.firma_vergi_dairesi.trim(),
                    firma_iban: firma.firma_iban.replace(/\s/g, "").toUpperCase(),
                  }
                : {}),
            }
          : {}),
      };
      let u;
      if (socialMode) {
        // Google ile giriş yapılmış; kimlik token'dan gelir, parola yok.
        u = await completeSocialRegistration(base);
        toast.success(`Hoş geldin, ${(u.ad_soyad || "").split(" ")[0] || "PAK üyesi"}!`);
      } else {
        u = await register({ ...base, email: form.email.trim().toLowerCase(), sifre: form.sifre });
        toast.success(`Hoş geldin, ${(u.ad_soyad || "").split(" ")[0] || "PAK üyesi"}! Doğrulama e-postası gönderildi.`);
      }
      navigate(nextUrl || (u.role === "admin" ? "/admin" : u.role === "saglayici" ? "/panel" : "/"), { replace: true });
    } catch (e) {
      toast.error(formatAuthError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AuthHeader />

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A]">
            {socialMode ? "Kaydınızı tamamlayın" : "PAK'a Kayıt Ol"}
          </h1>
          <p className="mt-2 text-slate-600">
            {socialMode ? (
              "Google ile giriş yaptınız — hesabınızı oluşturmak için birkaç bilgi daha alalım."
            ) : (
              <>
                Zaten hesabın var mı?{" "}
                <Link to={girisHref} className="text-[#10B981] font-semibold hover:underline" data-testid="link-login">
                  Giriş Yap
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Role selector */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <button
            type="button"
            onClick={() => setRole("musteri")}
            data-testid="role-musteri"
            className={`text-left rounded-2xl p-6 border transition-all ${
              role === "musteri"
                ? "border-[#10B981] bg-emerald-50/40 ring-2 ring-[#10B981]/20"
                : "border-slate-200 bg-white hover:border-[#10B981]/40"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${role === "musteri" ? "bg-[#10B981] text-white" : "bg-emerald-50 text-[#10B981]"}`}>
                <Users className="h-6 w-6" />
              </div>
              {role === "musteri" && <Check className="h-5 w-5 text-[#10B981]" />}
            </div>
            <p className="mt-4 font-semibold text-lg">Müşteriyim</p>
            <p className="text-sm text-slate-600 mt-1">Evime temizlik hizmeti almak istiyorum.</p>
          </button>

          <button
            type="button"
            onClick={() => setRole("saglayici")}
            data-testid="role-saglayici"
            className={`text-left rounded-2xl p-6 border transition-all ${
              role === "saglayici"
                ? "border-[#10B981] bg-emerald-50/40 ring-2 ring-[#10B981]/20"
                : "border-slate-200 bg-white hover:border-[#10B981]/40"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${role === "saglayici" ? "bg-[#10B981] text-white" : "bg-emerald-50 text-[#10B981]"}`}>
                <Briefcase className="h-6 w-6" />
              </div>
              {role === "saglayici" && <Check className="h-5 w-5 text-[#10B981]" />}
            </div>
            <p className="mt-4 font-semibold text-lg">Hizmet vermek istiyorum</p>
            <p className="text-sm text-slate-600 mt-1">Temizlik uzmanı olarak iş almak istiyorum.</p>
          </button>
        </div>

        {role && (
          <form onSubmit={onSubmit} className="rounded-2xl bg-white border border-slate-100 pak-shadow p-6 md:p-8 space-y-5 pak-animate-in" data-testid="register-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="mb-2 block">Ad Soyad</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={form.ad_soyad} onChange={setField("ad_soyad")} className="h-12 rounded-xl pl-11" placeholder="Ayşe Yılmaz" data-testid="reg-name" />
                </div>
              </div>
              {socialMode ? (
                <div className="sm:col-span-2 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-[#0F172A] flex items-center gap-2" data-testid="reg-social-email">
                  <Mail className="h-4 w-4 text-[#10B981] flex-shrink-0" />
                  Google ile giriş yaptınız{form.email ? `: ${form.email}` : ""}
                </div>
              ) : (
                <div>
                  <Label className="mb-2 block">E-posta</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="email" value={form.email} onChange={setField("email")} className="h-12 rounded-xl pl-11" placeholder="ornek@email.com" data-testid="reg-email" />
                  </div>
                </div>
              )}
              <div className={socialMode ? "sm:col-span-2" : ""}>
                <Label className="mb-2 block">Telefon</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={form.telefon} onChange={setField("telefon")} className="h-12 rounded-xl pl-11" placeholder="05XX XXX XX XX" data-testid="reg-phone" />
                </div>
              </div>
              {!socialMode && (
              <div>
                <Label className="mb-2 block">Şifre</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={form.sifre}
                    onChange={setField("sifre")}
                    className="h-12 rounded-xl pl-11 pr-11"
                    placeholder="En az 6 karakter"
                    data-testid="reg-password"
                  />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              )}
              {!socialMode && (
              <div>
                <Label className="mb-2 block">Şifre Tekrar</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={form.sifre_tekrar}
                    onChange={setField("sifre_tekrar")}
                    className="h-12 rounded-xl pl-11"
                    placeholder="Şifrenizi tekrar girin"
                    data-testid="reg-password-confirm"
                  />
                </div>
                {form.sifre_tekrar.length > 0 && (
                  form.sifre === form.sifre_tekrar ? (
                    <p className="mt-1.5 text-xs text-[#10B981] flex items-center gap-1" data-testid="pwd-match"><Check className="h-3.5 w-3.5" /> Şifreler eşleşti</p>
                  ) : (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1" data-testid="pwd-mismatch"><X className="h-3.5 w-3.5" /> Şifreler eşleşmiyor</p>
                  )
                )}
              </div>
              )}
            </div>

            {role === "saglayici" && (
              <div className="space-y-5 pt-4 border-t border-slate-100">
                <div>
                  <Label className="mb-3 block font-semibold">Hizmet alanı</Label>
                  <p className="text-xs text-slate-500 mb-3">Hangi alanda çalışıyorsunuz? Tek bir alan seçin — yalnız o alandaki talepler size gösterilir.</p>
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="reg-kategoriler">
                    {allCategories.map((c) => {
                      const on = kategoriler.includes(c);
                      return (
                        <button
                          type="button"
                          key={c}
                          onClick={() => setKategoriler([c])}
                          data-testid={`kategori-${c}`}
                          className={`text-left flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-all ${
                            on
                              ? "bg-emerald-50 text-[#10B981] border-[#10B981]"
                              : "bg-white text-slate-700 border-slate-200 hover:border-[#10B981]"
                          }`}
                        >
                          <span>{c}</span>
                          {on ? <Check className="h-4 w-4 text-[#10B981]" /> : <X className="h-4 w-4 text-slate-300" />}
                        </button>
                      );
                    })}
                  </div>
                  {/* Tipi kategoriden türetilir: Halı → Firma, Temizlik → Bireysel */}
                  <p className="mt-2 text-xs text-slate-500" data-testid="reg-tip-bilgi">
                    {isCarpet
                      ? "Halı & Koltuk Yıkama firma (kurumsal) hesabı olarak kaydedilir."
                      : "Ev Temizliği bireysel hesap olarak kaydedilir."}
                  </p>
                </div>

                {isCarpet && (
                  <div data-testid="reg-firma-bilgileri">
                    <Label className="mb-1 block font-semibold">Firma bilgileri</Label>
                    <p className="text-xs text-slate-500 mb-3">Halı & Koltuk Yıkama yalnız firma olarak sunulur; aşağıdaki bilgiler zorunludur.</p>
                    <div className="mt-1 space-y-3 rounded-xl bg-slate-50 border border-slate-100 p-4">
                        <div>
                          <Label className="mb-1.5 block text-sm font-medium">Firma adı <span className="text-rose-500">*</span></Label>
                          <Input
                            value={firma.firma_adi}
                            onChange={setFirmaField("firma_adi")}
                            placeholder="Örn. Pırıl Halı Yıkama Ltd. Şti."
                            data-testid="firma-adi"
                            className="h-11 rounded-xl"
                          />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="mb-1.5 block text-sm font-medium">Vergi numarası <span className="text-rose-500">*</span></Label>
                            <Input
                              value={firma.firma_vergi_no}
                              onChange={setFirmaField("firma_vergi_no")}
                              placeholder="10 haneli"
                              inputMode="numeric"
                              data-testid="firma-vergi-no"
                              className="h-11 rounded-xl"
                            />
                          </div>
                          <div>
                            <Label className="mb-1.5 block text-sm font-medium">Vergi dairesi <span className="text-rose-500">*</span></Label>
                            <Input
                              value={firma.firma_vergi_dairesi}
                              onChange={setFirmaField("firma_vergi_dairesi")}
                              placeholder="Örn. Bornova"
                              data-testid="firma-vergi-dairesi"
                              className="h-11 rounded-xl"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="mb-1.5 block text-sm font-medium">IBAN <span className="text-rose-500">*</span></Label>
                          <Input
                            value={firma.firma_iban}
                            onChange={setFirmaField("firma_iban")}
                            placeholder="TR__ ____ ____ ____ ____ ____ __"
                            data-testid="firma-iban"
                            className="h-11 rounded-xl"
                          />
                          <p className="mt-1 text-xs text-slate-500">Ödemeler bu hesaba aktarılacaktır (firma hesabı).</p>
                        </div>
                      </div>
                  </div>
                )}

                <div>
                  <Label className="mb-2 block font-semibold">Hizmet bölgeleri</Label>
                  <p className="text-xs text-slate-500 mb-3">Hizmet verdiğiniz il ve ilçeleri seçin (birden çok il ekleyebilirsiniz).</p>
                  <div data-testid="reg-bolgeler">
                    <ProviderRegionPicker value={bolgeler} onChange={setBolgeler} />
                  </div>
                </div>
                <div>
                  <Label className="mb-3 block font-semibold">Uzmanlık alanları</Label>
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="reg-uzmanliklar">
                    {activeSpecialties.map((s) => {
                      const on = uzmanliklar.includes(s);
                      return (
                        <button
                          type="button"
                          key={s}
                          onClick={() => toggle(uzmanliklar, setUzmanliklar, s)}
                          data-testid={`uzmanlik-${s}`}
                          className={`text-left flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-all ${
                            on
                              ? "bg-emerald-50 text-[#10B981] border-[#10B981]"
                              : "bg-white text-slate-700 border-slate-200 hover:border-[#10B981]"
                          }`}
                        >
                          <span>{s}</span>
                          {on ? <Check className="h-4 w-4 text-[#10B981]" /> : <X className="h-4 w-4 text-slate-300" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-100 space-y-3">
              {/* Onay gerektiren metinler (kutu işaretlenir) */}
              <ApprovalConsent
                title="KVKK Aydınlatma Metni"
                linkLabel="KVKK Aydınlatma Metni'ni"
                text={KVKK_TEXT}
                checked={kvkk}
                onChange={setKvkk}
                hint="Onay kutusuna basın; metin açılır, sonuna kadar okuyup onaylayın."
                testid="reg-kvkk"
              />
              <ApprovalConsent
                title="Açık Rıza Metni"
                linkLabel="Açık Rıza Metni'ni"
                text={ACIK_RIZA_TEXT}
                checked={acikRiza}
                onChange={setAcikRiza}
                hint="Onay kutusuna basın; metin açılır, sonuna kadar okuyup onaylayın."
                testid="reg-acik-riza"
              />

              {/* Bilgilendirme metinleri — okundu onayı (rıza değil) */}
              <ApprovalConsent
                title="Aydınlatma Metni"
                linkLabel="Aydınlatma Metni'ni"
                suffix="okudum."
                acceptLabel="Okudum"
                text={AYDINLATMA_TEXT}
                checked={aydinlatma}
                onChange={setAydinlatma}
                hint="Onay kutusuna basın; metin açılır, sonuna kadar okuyup kapatın."
                testid="reg-aydinlatma"
              />
              <ApprovalConsent
                title="Çerez Politikası"
                linkLabel="Çerez Politikası'nı"
                suffix="okudum."
                acceptLabel="Okudum"
                text={CEREZ_TEXT}
                checked={cerez}
                onChange={setCerez}
                hint="Onay kutusuna basın; metin açılır, sonuna kadar okuyup kapatın."
                testid="reg-cerez"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-[#10B981] hover:bg-[#0EA371] text-white font-semibold"
              data-testid="register-submit"
            >
              {submitting
                ? (socialMode ? "Tamamlanıyor..." : "Kayıt oluşturuluyor...")
                : <>{socialMode ? "Kaydı Tamamla" : "Kayıt Ol"} <ChevronRight className="ml-1 h-4 w-4" /></>}
            </Button>

            {!socialMode && (
              <>
                {/* Google girişi native uygulamada gizli — popup capacitor:// şemasında çalışmaz */}
                <div className="native-hide flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs text-slate-400">veya</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="native-hide">
                  <GoogleButton onClick={handleGoogle} loading={googleLoading} testid="register-google" />
                </div>
              </>
            )}

            {role === "saglayici" && (
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900 flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Hizmet veren hesapları, ekibimiz tarafından doğrulandıktan sonra herkese açık hale gelir. Onay sürecinde profiliniz "Beklemede" durumunda olur.</span>
              </div>
            )}
          </form>
        )}
      </main>
    </div>
  );
}
